import { inject, Injectable, OnDestroy } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { MonitoringProductService } from "@domain/product/monitoring/monitoring-product.service";
import { ProductService } from "@domain/product/product.service";
import { CentralStationConfiguration } from "@domain/project/configurations/monitoring/central-station-configuration";
import { KvmConfiguration } from "@domain/project/configurations/monitoring/kvm-configuration";
import { FloorplanPlaceholder } from "@domain/project/floorplan/floorplan-placeholder";
import { requireScopedProject } from "@domain/project/project-deserializer";
import { ProjectEvent, ProjectEventType } from "@domain/project/project-event";
import type { ScopedProject } from "@domain/project/project-guards";
import { isFgdsProject, isMonitoringProject } from "@domain/project/project-guards";
import { ProjectUpdatePublisher } from "@domain/project/project-update-publisher";
import { ProjectService } from "@domain/project/project.service";
import { ProductConfigurationService } from "@project/product-configuration.service";
import * as Sentry from "@sentry/angular";
import { ErrorReportingService } from "@utils/error-reporting.service";
import { catchError, concatMap, EMPTY, filter, map, Observable, ReplaySubject, retry, Subject, take, timeout } from "rxjs";

const AUTOSAVE_SAVE_TIMEOUT_MS = 15000;

@Injectable()
export class ProjectStateService implements ProjectUpdatePublisher, OnDestroy {
  private projectService = inject(ProjectService);
  private productService = inject(ProductService);
  private monitoringProductService = inject(MonitoringProductService);
  private activatedRoute = inject(ActivatedRoute);
  private productConfigurationService = inject(ProductConfigurationService);
  private errorReportingService = inject(ErrorReportingService);

  private projectEvents = new Subject<ProjectEvent>();
  private discontinuedConfigIds = new ReplaySubject<string[]>(1);
  private incompleteConfigIds = new ReplaySubject<string[]>(1);
  private currentIncompleteConfigIds: string[] = [];

  private readonly project: ScopedProject;

  constructor() {
    this.project = this.activatedRoute.snapshot.data["project"];
    this.init();
    this.updateDiscontinuedConfigIds();
    this.incompleteConfigIds.next([]);
    this.updateIncompleteConfigIds();
    this.initProjectEventHandler();
  }

  get projectEvents$(): Observable<ProjectEvent> {
    return this.projectEvents.asObservable();
  }

  get discontinuedConfigIds$() {
    return this.discontinuedConfigIds.asObservable();
  }

  get incompleteConfigIds$() {
    return this.incompleteConfigIds.asObservable();
  }

  isConfigDiscontinued(configId: string): Observable<boolean> {
    return this.discontinuedConfigIds.pipe(map((discontinuedConfigIds) => discontinuedConfigIds.includes(configId)));
  }

  isConfigIncomplete(configId: string): Observable<boolean> {
    return this.incompleteConfigIds.pipe(map((incompleteConfigIds) => incompleteConfigIds.includes(configId)));
  }

  publishProjectEvent(type: ProjectEventType, subject: any) {
    // Logs EVERY project change (config added/deleted/updated) as a breadcrumb.
    // Costs no Sentry quota, but is automatically attached to any later captureMessage/captureException
    // event (e.g. HASH-MISMATCH in project-card.component.ts).
    // This makes it possible to reconstruct whether an observed reduction in device count is
    // explainable by actual delete actions of the user, or is unexplained/a bug.
    Sentry.addBreadcrumb({
      message: `[project-event] ${type}`,
      level: "info",
      category: "project-event",
      data: { subjectId: subject?.id, projectId: this.project.id },
    });
    this.projectEvents.next({ project: this.project, type, subject });
  }

  updateDiscontinuedConfigIds() {
    if (isFgdsProject(this.project)) {
      this.productService
        .getDiscontinuedProductIds()
        .pipe(
          map((discontinuedProductIds) => {
            const discontinuedConfigIds = this.project.productConfigurations
              .filter((config) =>
                config.configuredProductIds.some((configuredProductId) => discontinuedProductIds.includes(configuredProductId)),
              )
              .map((config) => config.id);
            this.discontinuedConfigIds.next(discontinuedConfigIds);
          }),
        )
        .subscribe();
      return;
    }

    if (isMonitoringProject(this.project)) {
      this.monitoringProductService
        .getDiscontinuedMatNrs(this.productService.getFilterCountry())
        .pipe(
          map((discontinuedMatNrs) => {
            const discontinuedConfigIds = this.project.productConfigurations
              .filter((config) =>
                config.configuredProductIds.some((configuredProductId) => discontinuedMatNrs.includes(configuredProductId)),
              )
              .map((config) => config.id);
            this.discontinuedConfigIds.next(discontinuedConfigIds);
          }),
        )
        .subscribe();
      return;
    }

    this.discontinuedConfigIds.next([]);
  }

  updateIncompleteConfigIds() {
    if (!isFgdsProject(this.project)) {
      this.incompleteConfigIds.next([]);
      return;
    }
    this.productConfigurationService
      .getIncompleteConfigIds(this.project)
      .pipe(take(1))
      .subscribe((incompleteConfigIds) => {
        const union = Array.from(new Set([...(this.currentIncompleteConfigIds ?? []), ...(incompleteConfigIds ?? [])]));
        this.currentIncompleteConfigIds = union;
        this.incompleteConfigIds.next([...union]);
      });
  }

  addIncompleteConfigId(id: string) {
    if (!this.currentIncompleteConfigIds.includes(id)) {
      this.currentIncompleteConfigIds = [...this.currentIncompleteConfigIds, id];
      this.incompleteConfigIds.next([...this.currentIncompleteConfigIds]);
    }
  }

  removeIncompleteConfigId(id: string) {
    if (this.currentIncompleteConfigIds.includes(id)) {
      this.currentIncompleteConfigIds = this.currentIncompleteConfigIds.filter((i) => i !== id);
      this.incompleteConfigIds.next([...this.currentIncompleteConfigIds]);
    }
  }

  ngOnDestroy() {
    this.publishProjectEvent(ProjectEventType.PROJECT_CLOSED, this.project);
    this.project.updatePublisher = undefined;
    this.projectEvents.complete();
    this.discontinuedConfigIds.complete();
    this.incompleteConfigIds.complete();
  }

  private init() {
    this.project.updatePublisher = this;
    this.publishProjectEvent(ProjectEventType.PROJECT_OPENED, this.project);

    this.projectEvents$
      .pipe(
        filter((event) => event.type !== ProjectEventType.PROJECT_OPENED && event.type !== ProjectEventType.PROJECT_CLOSED),
        concatMap((event) =>
          this.projectService.update(requireScopedProject(event.project)).pipe(
            timeout(AUTOSAVE_SAVE_TIMEOUT_MS),
            retry({ count: 2, delay: 500 }),
            // The error must not reach the outer subscriber - it would terminate this subscription
            // and silently disable auto-save for the rest of the session.
            catchError((error) => {
              console.error("Failed to persist project change", error);
              this.errorReportingService.captureException(error, {
                tags: { area: "project-autosave" },
                extra: { projectId: event.project?.id, cloudId: event.project?.cloudId, eventType: event.type },
              });
              return EMPTY;
            }),
          ),
        ),
      )
      .subscribe();
  }

  private initProjectEventHandler() {
    this.projectEvents$.subscribe((event) => {
      switch (event.type) {
        case ProjectEventType.GAS_WARNING_CENTER_CONFIGURATION_UPDATED:
        case ProjectEventType.TRANSMITTER_CONFIGURATION_UPDATED:
        case ProjectEventType.ALARM_DEVICE_CONFIGURATION_UPDATED:
        case ProjectEventType.PLASTIC_SIGN_CONFIGURATION_UPDATED:
        case ProjectEventType.SIGNAL_ELEMENT_CONFIGURATION_UPDATED:
          this.updateDiscontinuedConfigIds();
          this.updateIncompleteConfigIds();
          break;
        case ProjectEventType.FLAME_DETECTOR_CONFIGURATION_UPDATED:
          this.updateDiscontinuedConfigIds();
          this.removeIncompleteConfigId((event.subject as any).id);
          this.updateIncompleteConfigIds();
          break;
        case ProjectEventType.TRANSMITTER_CONFIGURATION_ADDED:
        case ProjectEventType.PLASTIC_SIGN_CONFIGURATION_ADDED:
        case ProjectEventType.FLAME_DETECTOR_CONFIGURATION_ADDED:
          this.updateIncompleteConfigIds();
          break;
        case ProjectEventType.PATIENT_MONITOR_CONFIGURATION_UPDATED:
        case ProjectEventType.CENTRAL_STATION_CONFIGURATION_ADDED:
        case ProjectEventType.CENTRAL_STATION_CONFIGURATION_UPDATED:
          this.updateDiscontinuedConfigIds();
          this.removeIncompleteConfigId((event.subject as CentralStationConfiguration).id);
          this.updateIncompleteConfigIds();
          break;
        case ProjectEventType.TELEMETRY_CONFIGURATION_UPDATED:
        case ProjectEventType.DISPLAY_CONFIGURATION_UPDATED:
        case ProjectEventType.UTILITY_CONFIGURATION_UPDATED:
          this.removeIncompleteConfigId((event.subject as any).id);
          this.updateIncompleteConfigIds();
          break;
        case ProjectEventType.KVM_CONFIGURATION_UPDATED:
          this.removeIncompleteConfigId((event.subject as any).id);
          this.syncKvmReceivers(event.subject as KvmConfiguration);
          this.updateIncompleteConfigIds();
          break;
        case ProjectEventType.KVM_CONFIGURATION_ADDED:
          this.syncKvmReceivers(event.subject as KvmConfiguration);
          this.updateIncompleteConfigIds();
          break;
        case ProjectEventType.PLACEHOLDER_PRODUCTS_UPDATED:
          this.refreshPositionIdsAfterPlaceholderProductsUpdate(event.subject);
          break;
      }
    });
  }

  private syncKvmReceivers(config: KvmConfiguration) {
    this.project.cachedFloorplans.forEach((floorplan) => floorplan.syncKvmReceivers(config));
  }

  private refreshPositionIdsAfterPlaceholderProductsUpdate(subject: any) {
    if (this.usesPlaceholderPrefixPositionId(subject)) {
      this.project.refreshPlaceholderPositionIds(subject);
      return;
    }

    this.project.setPositionNumbers();
  }

  private usesPlaceholderPrefixPositionId(subject: any): subject is FloorplanPlaceholder {
    if (!(subject instanceof FloorplanPlaceholder)) {
      return false;
    }

    return subject.usesPlaceholderPrefixPositionId();
  }
}
